// Open-takenlijst-sortering voor de operationele takenmodule
// (docs/specs/34-operationele-takenmodule.md § 3).
//
// Pure, deterministische en stabiele sortering. Geen I/O. Wordt server-side
// gebruikt zodat backoffice- en portal-lijsten identiek ordenen.

import type { TaakPrioriteitOp } from './taak-escalatie'

/** HOOG eerst, dan MIDDEL, dan LAAG. */
const PRIO_RANK: Record<TaakPrioriteitOp, number> = { HOOG: 0, MIDDEL: 1, LAAG: 2 }

/**
 * Minimale vorm die `sorteerOpenTaken` nodig heeft. Concrete DTO's mogen meer
 * velden hebben — generic `T` behoudt het volledige rij-type in de uitvoer.
 */
export interface SorteerbareTaak {
  prioriteit: TaakPrioriteitOp
  /** 'yyyy-mm-dd' of null (null = geen deadline → achteraan). */
  deadline: string | null
  aantalKeerNietUitgevoerd: number
  /** ISO-timestamp; lexicografisch vergelijkbaar. */
  createdAt: string
}

/**
 * Sorteert een lijst open taken deterministisch en stabiel.
 *
 * Volgorde van sleutels (spec § 3):
 *  1. prioriteit  — HOOG < MIDDEL < LAAG (via `PRIO_RANK`).
 *  2. deadline    — oplopend, `null` als laatste (behandeld als `+Infinity`);
 *                   'yyyy-mm-dd' strings worden lexicografisch vergeleken, wat
 *                   voor dat formaat datum-correct is.
 *  3. aantalKeerNietUitgevoerd — aflopend (hoger eerst).
 *  4. createdAt   — oplopend (oudste eerst), ISO-strings lexicografisch.
 *
 * Geeft een nieuwe array terug; de invoer wordt niet gemuteerd.
 * Resterende volledige gelijkstand behoudt de invoervolgorde
 * (`Array.prototype.sort` is stabiel sinds ES2019).
 */
export function sorteerOpenTaken<T extends SorteerbareTaak>(taken: T[]): T[] {
  return [...taken].sort((a, b) => {
    // 1. prioriteit
    const prio = PRIO_RANK[a.prioriteit] - PRIO_RANK[b.prioriteit]
    if (prio !== 0) return prio

    // 2. deadline oplopend, nulls last
    const da = a.deadline ?? '￿' // sorteert ná elke geldige 'yyyy-mm-dd'
    const db = b.deadline ?? '￿'
    if (da !== db) return da < db ? -1 : 1

    // 3. aantalKeerNietUitgevoerd aflopend
    const fails = b.aantalKeerNietUitgevoerd - a.aantalKeerNietUitgevoerd
    if (fails !== 0) return fails

    // 4. createdAt oplopend (oudste eerst)
    if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1
    return 0
  })
}
